import { createAppError } from '../../api/errors';
import { walletFixtures, walletLedgerFixtures } from './fixtures';
import type { WalletRepository } from './repository';
import type {
  ApprovalRequestSummary,
  CreateWalletAdjustmentInput,
  WalletAdjustmentInput,
  WalletAdjustmentPreview,
  WalletDetail,
  WalletLedgerEntry,
  WalletListQuery,
  WalletSummary,
} from './types';
import { compareWalletDecimals } from './types';

export interface MockWalletRepositoryOptions {
  readonly empty?: boolean;
  readonly listError?: boolean;
  readonly detailError?: boolean;
  readonly entriesError?: boolean;
  readonly statusError?: boolean;
  readonly previewError?: boolean;
  readonly adjustmentError?: boolean;
}

// Stable, human-readable route identifiers resolve to the first fixture without duplicating
// fixture rows or altering list/pagination totals.
const walletDetailAliases: Readonly<Record<string, string>> = {
  'wallet-a': walletFixtures[0]!.id,
};

export class MockWalletRepository implements WalletRepository {
  readonly #options: MockWalletRepositoryOptions;
  #wallets: WalletDetail[];
  readonly #previews = new Map<
    string,
    { readonly fingerprint: string; readonly preview: WalletAdjustmentPreview }
  >();
  readonly #adjustments = new Map<
    string,
    {
      readonly fingerprint: string;
      readonly result: { readonly data: ApprovalRequestSummary; readonly requestId: string };
    }
  >();

  constructor(options: MockWalletRepositoryOptions = {}) {
    this.#options = options;
    this.#wallets = options.empty === true ? [] : walletFixtures.map((wallet) => ({ ...wallet }));
  }

  async list(query: WalletListQuery, signal: AbortSignal) {
    abortIfNeeded(signal);
    if (this.#options.listError === true) throw mockError('MOCK_WALLET_LIST_FAILED', 'list');
    const filtered = this.#wallets
      .filter((wallet) => matchesWallet(wallet, query))
      .toSorted((left, right) =>
        compareDate(right[query.sort], left[query.sort], right.id, left.id),
      );
    const start = parseCursor(query.cursor);
    const items = filtered.slice(start, start + query.pageSize);
    const next = start + items.length;
    return {
      items,
      nextCursor: next < filtered.length ? `mock-wallet-cursor-${next}` : null,
      requestId: 'mock-wallet-list',
    };
  }

  async get(walletId: string, signal: AbortSignal) {
    abortIfNeeded(signal);
    if (this.#options.detailError === true) throw mockError('MOCK_WALLET_DETAIL_FAILED', 'detail');
    const resolvedId = walletDetailAliases[walletId] ?? walletId;
    const wallet = this.#wallets.find((candidate) => candidate.id === resolvedId);
    if (wallet === undefined) throw notFound(walletId);
    return wallet;
  }

  async listEntries(
    walletId: string,
    query: Parameters<WalletRepository['listEntries']>[1],
    signal: AbortSignal,
  ) {
    abortIfNeeded(signal);
    if (this.#options.entriesError === true)
      throw mockError('MOCK_WALLET_ENTRIES_FAILED', 'entries');
    if (!this.#wallets.some((wallet) => wallet.id === walletId)) throw notFound(walletId);
    const filtered = walletLedgerFixtures
      .filter((entry) => entry.walletId === walletId && matchesEntry(entry, query.filters))
      .toSorted((left, right) =>
        compareDate(right[query.sort], left[query.sort], right.id, left.id),
      );
    const start = parseEntryCursor(query.cursor);
    const items = filtered.slice(start, start + query.pageSize);
    const next = start + items.length;
    return {
      items,
      nextCursor: next < filtered.length ? `mock-entry-cursor-${next}` : null,
      requestId: 'mock-wallet-entries',
    };
  }

  async changeStatus(walletId: string, input: Parameters<WalletRepository['changeStatus']>[1]) {
    if (this.#options.statusError === true) throw mockError('MOCK_WALLET_STATUS_FAILED', 'status');
    const index = this.#wallets.findIndex((wallet) => wallet.id === walletId);
    if (index < 0) throw notFound(walletId);
    const current = this.#wallets[index]!;
    const updated: WalletDetail = {
      ...current,
      status: input.status,
      version: String(BigInt(current.version) + 1n),
      updatedAt: '2026-07-23T12:00:00+07:00',
    };
    this.#wallets = this.#wallets.with(index, updated);
    return { data: updated, requestId: 'mock-wallet-status' };
  }

  async previewAdjustment(walletId: string, input: WalletAdjustmentInput) {
    if (this.#options.previewError === true)
      throw mockError('MOCK_WALLET_ADJUSTMENT_PREVIEW_FAILED', 'adjustment-preview');
    const wallet = this.#wallets.find((candidate) => candidate.id === walletId);
    if (wallet === undefined) throw notFound(walletId);
    validateAdjustmentInput(input);
    const preview: WalletAdjustmentPreview = {
      walletId,
      currencyCode: wallet.currencyCode,
      signedAmount: input.signedAmount,
      availableBefore: wallet.availableBalance,
      availableAfter: addExactDecimals(wallet.availableBalance, input.signedAmount),
      sourceTransactionId: input.sourceTransactionId ?? null,
      previewHash: 'wallet_adjustment_preview_hash_000000000000',
    };
    this.#previews.set(walletId, { fingerprint: adjustmentFingerprint(input), preview });
    return preview;
  }

  async createAdjustment(
    walletId: string,
    input: CreateWalletAdjustmentInput,
    idempotencyKey: string,
  ) {
    if (this.#options.adjustmentError === true)
      throw mockError('MOCK_WALLET_ADJUSTMENT_FAILED', 'adjustment');
    const commandFingerprint = JSON.stringify({ walletId, input });
    const prior = this.#adjustments.get(idempotencyKey);
    if (prior !== undefined) {
      if (prior.fingerprint === commandFingerprint) return prior.result;
      throw mockError('MOCK_WALLET_IDEMPOTENCY_CONFLICT', 'adjustment');
    }
    if (!uuidV4Pattern.test(idempotencyKey))
      throw mockError('MOCK_WALLET_IDEMPOTENCY_INVALID', 'adjustment');
    const wallet = this.#wallets.find((candidate) => candidate.id === walletId);
    if (wallet === undefined) throw notFound(walletId);
    const preview = this.#previews.get(walletId);
    const { previewHash, ...previewInput } = input;
    if (
      preview === undefined ||
      preview.preview.previewHash !== previewHash ||
      preview.fingerprint !== adjustmentFingerprint(previewInput)
    ) {
      throw mockError('MOCK_WALLET_PREVIEW_STALE', 'adjustment');
    }
    const result = {
      data: {
        id: `00000000-0000-4000-8000-${String(this.#adjustments.size + 1401).padStart(12, '0')}`,
        partnerId: wallet.partner.id,
        actionType: 'WALLET_ADJUST',
        entityType: 'AFFILIATE_WALLET',
        entityId: wallet.id,
        reason: input.reason,
        evidenceReference: input.evidenceReference,
        status: 'PENDING',
        requestedBy: '00000000-0000-4000-8000-000000000001',
        requestedAt: '2026-07-23T12:00:00+07:00',
        expiresAt: '2026-07-24T12:00:00+07:00',
        decidedBy: null,
        decidedAt: null,
        decisionReason: null,
        executedAt: null,
        executionReference: null,
      } satisfies ApprovalRequestSummary,
      requestId: 'mock-wallet-adjustment',
    };
    this.#adjustments.set(idempotencyKey, { fingerprint: commandFingerprint, result });
    return result;
  }
}

const signedAmountPattern = /^-?(?:0|[1-9][0-9]{0,19})(?:\.[0-9]{1,6})?$/u;
const reasonCodePattern = /^[A-Z][A-Z0-9_]{1,99}$/u;
const uuidV4Pattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;

function validateAdjustmentInput(input: WalletAdjustmentInput) {
  if (
    !signedAmountPattern.test(input.signedAmount) ||
    compareWalletDecimals(input.signedAmount, '0') === 0 ||
    !reasonCodePattern.test(input.reasonCode) ||
    input.reason.trim().length === 0 ||
    input.reason.length > 2000 ||
    input.evidenceReference.trim().length === 0 ||
    input.evidenceReference.length > 1000
  ) {
    throw mockError('MOCK_WALLET_ADJUSTMENT_INVALID', 'adjustment-preview');
  }
}

function adjustmentFingerprint(input: WalletAdjustmentInput) {
  return JSON.stringify({
    signedAmount: input.signedAmount,
    reasonCode: input.reasonCode,
    reason: input.reason,
    evidenceReference: input.evidenceReference,
    sourceTransactionId: input.sourceTransactionId ?? null,
  });
}

/** DEV mock-backend arithmetic only; production balance facts always come from the API. */
function addExactDecimals(left: string, right: string): string {
  const a = decimalParts(left);
  const b = decimalParts(right);
  const scale = Math.max(a.scale, b.scale);
  const sum = a.units * 10n ** BigInt(scale - a.scale) + b.units * 10n ** BigInt(scale - b.scale);
  const negative = sum < 0n;
  const absolute = negative ? -sum : sum;
  const digits = String(absolute).padStart(scale + 1, '0');
  const whole = scale === 0 ? digits : digits.slice(0, -scale);
  const fraction = scale === 0 ? '' : `.${digits.slice(-scale)}`;
  return `${negative ? '-' : ''}${whole}${fraction}`;
}

function decimalParts(value: string) {
  const match = /^(-)?(0|[1-9][0-9]*)(?:\.([0-9]+))?$/u.exec(value);
  if (match === null) throw new Error('Expected a canonical decimal string');
  const fraction = match[3] ?? '';
  const magnitude = BigInt(`${match[2] ?? '0'}${fraction}`);
  return {
    units: match[1] === '-' ? -magnitude : magnitude,
    scale: fraction.length,
  };
}

function matchesWallet(wallet: WalletSummary, query: WalletListQuery) {
  const filters = query.filters;
  return (
    matchesId(wallet.partner.id, filters.partnerId) &&
    matchesId(wallet.agent?.id ?? null, filters.agentId) &&
    matchesId(wallet.memberId, filters.memberId) &&
    includes(wallet.username, filters.username) &&
    includes(wallet.partnerUserId, filters.partnerUserId) &&
    (filters.currency === '' || wallet.currencyCode === filters.currency.trim().toUpperCase()) &&
    (filters.status === '' || wallet.status === filters.status) &&
    (filters.reconciliationStatus === '' ||
      wallet.reconciliation.status === filters.reconciliationStatus) &&
    minimum(wallet.availableBalance, filters.minimumAvailableBalance) &&
    maximum(wallet.availableBalance, filters.maximumAvailableBalance) &&
    minimum(wallet.reservedBalance, filters.minimumReservedBalance) &&
    maximum(wallet.reservedBalance, filters.maximumReservedBalance)
  );
}

function matchesEntry(
  entry: WalletLedgerEntry,
  filters: Parameters<WalletRepository['listEntries']>[1]['filters'],
) {
  const effective = Date.parse(entry.effectiveAt);
  return (
    (filters.entryType === '' || entry.entryType === filters.entryType) &&
    matchesId(entry.source.transactionId, filters.sourceTransactionId) &&
    matchesId(entry.source.withdrawalId, filters.sourceWithdrawalId) &&
    matchesId(entry.source.walletAdjustmentId, filters.sourceAdjustmentId) &&
    minimum(entry.amount, filters.minimumAmount) &&
    maximum(entry.amount, filters.maximumAmount) &&
    (filters.effectiveFrom === '' ||
      effective >= Date.parse(`${filters.effectiveFrom}:00+07:00`)) &&
    (filters.effectiveTo === '' || effective < Date.parse(`${filters.effectiveTo}:00+07:00`))
  );
}

function includes(value: string | null, filter: string) {
  return filter.trim() === '' || (value ?? '').toLowerCase().includes(filter.trim().toLowerCase());
}

function matchesId(value: string | null, filter: string) {
  return filter.trim() === '' || value === filter.trim();
}

function minimum(value: string, filter: string) {
  return filter.trim() === '' || compareWalletDecimals(value, filter) >= 0;
}

function maximum(value: string, filter: string) {
  return filter.trim() === '' || compareWalletDecimals(value, filter) <= 0;
}

function compareDate(left: string, right: string, leftId: string, rightId: string) {
  const comparison = Date.parse(left) - Date.parse(right);
  return comparison === 0 ? leftId.localeCompare(rightId) : comparison;
}

function parseCursor(cursor: string) {
  if (cursor === '') return 0;
  const match = /^mock-wallet-cursor-(\d+)$/u.exec(cursor);
  return match === null ? 0 : Number(match[1]);
}

function parseEntryCursor(cursor: string) {
  if (cursor === '') return 0;
  const match = /^mock-entry-cursor-(\d+)$/u.exec(cursor);
  return match === null ? 0 : Number(match[1]);
}

function abortIfNeeded(signal: AbortSignal) {
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
}

function mockError(code: string, surface: string) {
  return createAppError({
    status: 500,
    code,
    message: `Internal mock wallet ${surface} detail`,
    requestId: `mock-wallet-${surface}-request`,
    fieldErrors: {},
    retryAfterSeconds: null,
  });
}

function notFound(walletId: string) {
  return createAppError({
    status: 404,
    code: 'WALLET_NOT_FOUND',
    message: `Wallet ${walletId} was not found`,
    requestId: 'mock-wallet-not-found',
    fieldErrors: {},
    retryAfterSeconds: null,
  });
}
